import type {ReactElement, ReactNode} from 'react';
import {useRef, useState} from 'react';
import type {AiChatMemoryCardProps} from './ai-chat-memory-card.types';
import {memoryCardOptionsPanelId} from './ai-chat-memory-card.types';
import styles from './ai-chat-memory-card.module.scss';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {ClickableDiv} from '@Components/clickable-div';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {ColorTypes, ControlledListItemType7} from '@Components/controlled-list/components/controlled-list-item-type-7';
import {closePanel} from '@Components/panel/panel-reducer';
import {useAppDispatch} from '@/hooks';
import {openConfirmDeleteModal} from '@Modals/confirm-delete-modal/confirm-delete-modal.types';
import {AiChatTestId} from '@/test-ids/ai-chat-test-id';
import {useIsTextTruncated} from '@Hooks/useIsTextTruncated';

export function AiChatMemoryCard({memoryId, heading, body, imageUrl, deleteConfirmationText, onDelete}: AiChatMemoryCardProps): ReactElement {
  const dispatch = useAppDispatch();
  const optionsPanelId = memoryCardOptionsPanelId(memoryId);
  const bodyRef = useRef<HTMLSpanElement>(null);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const isBodyTruncated = useIsTextTruncated(bodyRef, body, isExpanded);

  /** On desktop the menu is an inline popup with no panel, and closing one that was never opened throws. */
  const closeOptionsMenuIfItIsAPanel = (): void => {
    if (window.PMW.redux.store.getState().panels.panelHashmap[optionsPanelId]) {
      dispatch(closePanel(optionsPanelId));
    }
  };

  const handleDelete = (): void => {
    closeOptionsMenuIfItIsAPanel();

    openConfirmDeleteModal({
      onDeleteConfirmation: onDelete,
      text: deleteConfirmationText,
      ctaText: window.i18next.t('pmwjs_delete'),
      title: window.i18next.t('pmwjs_ai_memory_delete_modal_title'),
    });
  };

  const getOptionsSelector = (): ReactElement => {
    return (
      <Icon
        data-testid={AiChatTestId.MEMORY_CARD_OPTIONS}
        type={IconType.TRANSPARENT}
        size={IconSize.SIZE_ICON_16}
        shape={IconShape.SQUARE}
        icon="icon-more-h"
        className={styles.moreIcon}
        ariaLabel={window.i18next.t('pmwjs_ai_memory_options')}
      />
    );
  };

  const getOptionsPopup = (): ReactElement => {
    return (
      <ul className={styles.optionsList}>
        <ControlledListItemType7
          data-testid={AiChatTestId.MEMORY_CARD_DELETE}
          id="delete"
          text={window.i18next.t('pmwjs_delete')}
          className="corner-radius-8"
          icon="icon-delete"
          color={ColorTypes.danger}
          onClick={handleDelete}
        />
      </ul>
    );
  };

  const getOptions = (): ReactElement => {
    return (
      <div className={styles.optionsWrapper}>
        <Dropdown
          key={optionsPanelId}
          id={optionsPanelId}
          doesCloseOnOuterClick
          doCloseOnInnerClick
          showModalForSmallScreen
          panelIdForModal={optionsPanelId}
          modalTitle={window.i18next.t('pmwjs_ai_memory_options')}
          selectorClassNameOnOpen="-active"
          selector={getOptionsSelector()}
          popup={getOptionsPopup()}
          className={styles.dropdown}
          alignment={ALIGNMENT_TYPE.RIGHT_START}
          position={DROPDOWN_POSITION.FIXED}
          popupClassName={styles.popup}
        />
      </div>
    );
  };

  const getExpandToggle = (): ReactNode => {
    if (!isBodyTruncated) {
      return null;
    }

    return (
      <ClickableDiv
        className={styles.expandToggle}
        onClick={(): void => {
          setIsExpanded((wasExpanded) => !wasExpanded);
        }}
      >
        <Text size={TextSize.SM} bold val={isExpanded ? window.i18next.t('pmwjs_carousel_show_less') : window.i18next.t('pmwjs_show_more')} />
      </ClickableDiv>
    );
  };

  const getThumbnail = (): ReactNode => {
    if (!imageUrl) {
      return null;
    }

    return <img className={styles.thumbnail} src={imageUrl} alt={heading} loading="lazy" />;
  };

  return (
    <div data-testid={AiChatTestId.MEMORY_CARD} className={`spacing-p-4 ${styles.card}`}>
      <div className={styles.cardHeader}>
        <Text className={`_unmargin ${styles.heading}`} size={TextSize.SM} bold val={heading} />
        {getOptions()}
      </div>
      {getThumbnail()}
      <Text ref={bodyRef} className={`_unmargin ${styles.body} ${isExpanded ? styles.bodyExpanded : ''}`} size={TextSize.MD} val={body} />
      {getExpandToggle()}
    </div>
  );
}
